import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronUpDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

// #region ComboboxProps
export interface ComboboxProps<T extends Record<string, any>> {
    /** Currently selected key value, or null when nothing is selected. */
    value: string | number | null;
    /** Called with the new key value (or null when the nullable "All" entry is chosen). */
    onChange: (value: string | number | null) => void;
    /** Source list of option objects. */
    options: T[];
    /** Key on each option whose value is shown to the user. */
    labelKey: keyof T;
    /** Key on each option whose value is reported through onChange. Defaults to 'id'. */
    valueKey?: keyof T;
    /** Label shown on the trigger when nothing is selected, and on the leading "null" entry. */
    placeholder?: string;
    /** Placeholder text inside the search input. */
    searchPlaceholder?: string;
    /** When true, inserts a "null" entry at the top of the list. Selecting it calls onChange(null). */
    nullable?: boolean;
    /** Optional explicit label for the null entry. Falls back to `placeholder` ("All ..."). */
    nullLabel?: string;
    /** Disables the trigger. */
    disabled?: boolean;
    /** Wrapper className. */
    className?: string;
    /** Override the trigger button styling. */
    triggerClassName?: string;
    /** ARIA label for the trigger when no visible label exists. */
    ariaLabel?: string;
    /**
     * ASYNC MODE. Supply the search term to the parent (which owns the request)
     * instead of filtering `options` locally. When set, `options` is rendered
     * verbatim — the substring filter is skipped entirely, because the server has
     * already done the matching and local filtering would discard valid rows.
     */
    onSearchChange?: (query: string) => void;
    /** Async mode: current search term, owned by the parent. */
    searchValue?: string;
    /** Async mode: show a loading row while a request is in flight. */
    loading?: boolean;
    /** Async mode: true when more rows exist past the ones supplied. */
    hasMore?: boolean;
    /** Async mode: fetch the next page. Called by the "Load more" row. */
    onLoadMore?: () => void;
    /**
     * Async mode: label for the selected value. REQUIRED in async mode — the
     * selected option is usually absent from the current result page, so it cannot
     * be derived from `options`.
     */
    selectedLabel?: string;
    /** Text shown when there are no rows. */
    emptyLabel?: string;
}

const NULL_KEY = '__combobox_null__';

// #region Combobox
/**
 * Searchable single-select dropdown. Click trigger to open a popup with an in-list
 * search input, keyboard navigation, and outside-click dismiss. Use for moderately
 * long pick lists (brands, designs, materials) where the native <select> stops being
 * usable.
 *
 * Two modes:
 * - SYNC (default): `options` holds the whole list; typing filters it locally.
 * - ASYNC (`onSearchChange` supplied): the parent fetches matches per keystroke.
 *   Local filtering is disabled and `selectedLabel` supplies the trigger text,
 *   since the selected row is usually not in the current page of results.
 */
export function Combobox<T extends Record<string, any>>({
    value,
    onChange,
    options,
    labelKey,
    valueKey,
    placeholder = 'Select...',
    searchPlaceholder = 'Search...',
    nullable = false,
    nullLabel,
    disabled = false,
    className = '',
    triggerClassName,
    ariaLabel,
    onSearchChange,
    searchValue,
    loading = false,
    hasMore = false,
    onLoadMore,
    selectedLabel: selectedLabelProp,
    emptyLabel = 'No matches',
}: ComboboxProps<T>): JSX.Element {
    const vKey = (valueKey ?? ('id' as keyof T)) as keyof T;
    const isAsync = typeof onSearchChange === 'function';

    const [open, setOpen] = useState(false);
    const [internalQuery, setInternalQuery] = useState('');
    const [activeIdx, setActiveIdx] = useState(0);
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    // In async mode the parent owns the term so it can debounce and cancel.
    const query = isAsync ? (searchValue ?? '') : internalQuery;

    // Normalize options to a uniform { key, label } shape, with the optional null
    // sentinel prepended. In async mode the rows are used as-is: the server did the
    // matching, and filtering again here would drop rows the server deemed a match.
    type Row = { key: string; label: string; isNull: boolean };
    const rows = useMemo<Row[]>(() => {
        const q = query.trim().toLowerCase();
        const mapped = options.map((o) => ({
            key: String(o[vKey]),
            label: String(o[labelKey] ?? ''),
            isNull: false,
        }));
        const matches = isAsync ? mapped : mapped.filter((r) => (q ? r.label.toLowerCase().includes(q) : true));
        if (nullable) {
            return [{ key: NULL_KEY, label: nullLabel ?? placeholder, isNull: true }, ...matches];
        }
        return matches;
    }, [options, labelKey, vKey, nullable, nullLabel, placeholder, query, isAsync]);

    const selectedLabel = useMemo(() => {
        if (value == null) return nullable ? (nullLabel ?? placeholder) : placeholder;
        // Async: the selected row is usually outside the current page, so an
        // options lookup would fall back to the placeholder and the trigger would
        // read "All Brands" while a brand is actually selected.
        if (isAsync) return selectedLabelProp || placeholder;
        const hit = options.find((o) => String(o[vKey]) === String(value));
        return hit ? String(hit[labelKey] ?? placeholder) : placeholder;
    }, [options, labelKey, vKey, value, nullable, nullLabel, placeholder, isAsync, selectedLabelProp]);

    // Close on outside click / escape.
    useEffect(() => {
        if (!open) return;
        const onPointer = (e: MouseEvent | TouchEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onPointer, true);
        document.addEventListener('touchstart', onPointer, true);
        document.addEventListener('keydown', onKey, true);
        return () => {
            document.removeEventListener('mousedown', onPointer, true);
            document.removeEventListener('touchstart', onPointer, true);
            document.removeEventListener('keydown', onKey, true);
        };
    }, [open]);

    // Keep the active index inside the visible row range when filtering shrinks the list.
    useEffect(() => {
        if (activeIdx >= rows.length) setActiveIdx(Math.max(0, rows.length - 1));
    }, [rows.length, activeIdx]);

    const setSearch = (next: string) => {
        if (onSearchChange) onSearchChange(next);
        else setInternalQuery(next);
        setActiveIdx(0);
    };

    const commit = (row: Row) => {
        onChange(row.isNull ? null : (rowValueFromKey(row.key) as string | number));
        setOpen(false);
        setSearch('');
    };

    // Resolve the raw value from the stringified key. We stringify on the way in
    // and need to coerce back: if every option's value coerces to Number cleanly,
    // return a number; otherwise leave it as the string we stored.
    const rowValueFromKey = (key: string): string | number => {
        const n = Number(key);
        return Number.isFinite(n) && String(n) === key ? n : key;
    };

    const onTriggerClick = () => {
        if (disabled) return;
        setOpen((o) => !o);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActiveIdx((i) => Math.min(rows.length - 1, i + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx((i) => Math.max(0, i - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (rows[activeIdx]) commit(rows[activeIdx]);
        }
    };

    const baseTrigger =
        'flex min-w-[10rem] items-center justify-between gap-2 rounded-lg border border-gray-300 dark:border-stone-600 bg-surface-alt px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 hover:bg-surface-muted dark:hover:bg-stone-700 disabled:opacity-50';

    // In async mode the null sentinel is always present even while loading, so
    // "only the sentinel" still counts as an empty result set.
    const hasResults = nullable ? rows.length > 1 : rows.length > 0;

    return (
        <div ref={wrapRef} className={`relative ${className}`}>
            <button
                type="button"
                onClick={onTriggerClick}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
                className={triggerClassName ?? baseTrigger}
            >
                <span className="truncate">{selectedLabel}</span>
                <ChevronUpDownIcon className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" />
            </button>

            {open && (
                <div className="absolute right-0 z-40 mt-1 w-64 max-w-[calc(100vw-16px)] overflow-hidden rounded-lg border border-gray-200 dark:border-stone-700 bg-surface-alt shadow-lg">
                    <div className="flex items-center gap-2 border-b border-gray-200 dark:border-stone-700 px-2 py-1.5">
                        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={onSearchKeyDown}
                            placeholder={searchPlaceholder}
                            className="w-full bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none"
                            aria-label={searchPlaceholder}
                            autoFocus
                        />
                        {query && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSearch('');
                                    inputRef.current?.focus();
                                }}
                                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                                aria-label="Clear search"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    <ul role="listbox" className="max-h-64 overflow-y-auto py-1">
                        {rows.map((row, idx) => {
                            const isSelected = row.isNull ? value == null : String(value) === row.key;
                            const isActive = idx === activeIdx;
                            return (
                                <li
                                    key={row.key}
                                    role="option"
                                    aria-selected={isSelected}
                                    onMouseEnter={() => setActiveIdx(idx)}
                                    onClick={() => commit(row)}
                                    className={`cursor-pointer truncate px-3 py-1.5 text-sm ${
                                        isActive
                                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                            : 'text-gray-700 dark:text-gray-200'
                                    } ${isSelected ? 'font-semibold' : ''}`}
                                >
                                    {row.label}
                                </li>
                            );
                        })}
                        {loading && (
                            <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Searching…</li>
                        )}
                        {!loading && !hasResults && (
                            <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">{emptyLabel}</li>
                        )}
                        {!loading && hasMore && onLoadMore && (
                            <li>
                                <button
                                    type="button"
                                    onClick={onLoadMore}
                                    className="w-full px-3 py-1.5 text-left text-xs font-medium text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
                                >
                                    Load more
                                </button>
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
